/**
 * AIT file reader implementation.
 */

import { AITBundle, AITMetadata, Permission } from "@apps-in-toss/ait-format-proto";
import { FORMAT_VERSION, LENGTH_SIZE, MAGIC, MAGIC_SIZE, VERSION_SIZE } from "./constants";
import { AITFormatError, AITVersionError } from "./errors";

export interface AITReaderOptions {
  /** Tuple of supported format versions */
  supportedVersions?: number[];
}

/**
 * Reader for AIT (Apps-in-Toss) bundle files.
 *
 * @example
 * ```typescript
 * const reader = AITReader.fromBuffer(buffer);
 * console.log(reader.deploymentId);
 * console.log(reader.appName);
 * console.log(reader.metadata);
 * const data = await reader.readEntry("index.html");
 * ```
 */
export class AITReader {
  private readonly buffer: Uint8Array;
  private readonly view: DataView;
  private readonly supportedVersions: number[];

  private _formatVersion: number = 0;
  private _bundle: AITBundle | null = null;
  private _zipBlobOffset: number = 0;
  private _zipBlobLength: number = 0;

  private constructor(buffer: Uint8Array, options?: AITReaderOptions) {
    this.buffer = buffer;
    this.view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);
    this.supportedVersions = options?.supportedVersions ?? [FORMAT_VERSION];

    this.readHeader();
    this.readBundle();
    this.readZipHeader();
  }

  /**
   * Create an AITReader from a buffer.
   */
  static fromBuffer(buffer: Uint8Array, options?: AITReaderOptions): AITReader {
    return new AITReader(buffer, options);
  }

  /**
   * Create an AITReader from an ArrayBuffer.
   */
  static fromArrayBuffer(buffer: ArrayBuffer, options?: AITReaderOptions): AITReader {
    return new AITReader(new Uint8Array(buffer), options);
  }

  private readHeader(): void {
    // Validate minimum size
    if (this.buffer.length < MAGIC_SIZE + VERSION_SIZE + LENGTH_SIZE) {
      throw new AITFormatError("Buffer too small to be a valid AIT file");
    }

    // Validate magic
    const magic = this.buffer.slice(0, MAGIC_SIZE);
    if (!this.arraysEqual(magic, MAGIC)) {
      throw new AITFormatError(
        `Invalid magic bytes: expected AITBUNDL, got ${new TextDecoder().decode(magic)}`
      );
    }

    // Read version
    this._formatVersion = this.view.getUint32(MAGIC_SIZE, false);
    if (!this.supportedVersions.includes(this._formatVersion)) {
      throw new AITVersionError(
        `Unsupported format version: ${this._formatVersion}, supported: [${this.supportedVersions.join(", ")}]`
      );
    }
  }

  private readBundle(): void {
    const bundleLenOffset = MAGIC_SIZE + VERSION_SIZE;
    const bundleLen = Number(this.view.getBigUint64(bundleLenOffset, false));

    const bundleOffset = bundleLenOffset + LENGTH_SIZE;
    if (this.buffer.length < bundleOffset + bundleLen) {
      throw new AITFormatError("Unexpected end of buffer reading bundle");
    }

    const bundleBytes = this.buffer.slice(bundleOffset, bundleOffset + bundleLen);
    this._bundle = AITBundle.decode(bundleBytes);

    if (!this._bundle.deploymentId) {
      throw new AITFormatError("Bundle deployment_id must not be empty");
    }
    if (!this._bundle.appName) {
      throw new AITFormatError("Bundle app_name must not be empty");
    }

    // Update offset for ZIP header
    this._zipBlobOffset = bundleOffset + bundleLen;
  }

  private readZipHeader(): void {
    if (this.buffer.length < this._zipBlobOffset + LENGTH_SIZE) {
      throw new AITFormatError("Unexpected end of buffer reading ZIP blob length");
    }

    this._zipBlobLength = Number(this.view.getBigUint64(this._zipBlobOffset, false));
    this._zipBlobOffset += LENGTH_SIZE; // Now points to actual ZIP data
  }

  private arraysEqual(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  /** Get the format version */
  get formatVersion(): number {
    return this._formatVersion;
  }

  /** Get the deployment ID */
  get deploymentId(): string {
    return this._bundle?.deploymentId ?? "";
  }

  /** Get the application name */
  get appName(): string {
    return this._bundle?.appName ?? "";
  }

  /** Get the bundle */
  get bundle(): AITBundle {
    if (!this._bundle) {
      throw new AITFormatError("Bundle not loaded");
    }
    return this._bundle;
  }

  /** Get the metadata */
  get metadata(): AITMetadata | undefined {
    return this._bundle?.metadata;
  }

  /** Get the permissions */
  get permissions(): Permission[] {
    return this._bundle?.permissions ?? [];
  }

  /**
   * Read the entire ZIP blob.
   */
  readZipBlob(): Uint8Array {
    return this.buffer.slice(this._zipBlobOffset, this._zipBlobOffset + this._zipBlobLength);
  }

  /**
   * Read a single entry from the ZIP blob.
   */
  async readEntry(name: string): Promise<Uint8Array> {
    const { unzipSync } = await import("fflate");
    const zipBlob = this.readZipBlob();
    const unzipped = unzipSync(zipBlob);

    const entry = unzipped[name];
    if (!entry) {
      throw new Error(`Entry not found: ${name}`);
    }

    return entry;
  }

  /**
   * List all entry names in the ZIP blob.
   */
  listEntries(): string[] {
    return this._bundle?.index.map((entry) => entry.name) ?? [];
  }

  /**
   * Read the signature if present.
   */
  readSignature(): Uint8Array | null {
    const sigLenOffset = this._zipBlobOffset + this._zipBlobLength;
    if (this.buffer.length < sigLenOffset + LENGTH_SIZE) {
      return null;
    }

    const sigLen = Number(this.view.getBigUint64(sigLenOffset, false));
    if (sigLen === 0) {
      return null;
    }

    const sigOffset = sigLenOffset + LENGTH_SIZE;
    if (this.buffer.length < sigOffset + sigLen) {
      return null;
    }

    return this.buffer.slice(sigOffset, sigOffset + sigLen);
  }

  /**
   * Convert bundle to app.json format.
   *
   * @returns Object in app.json format with appName, permissions, and _metadata.
   */
  toAppJson(): AppJson {
    const result: AppJson = {
      appName: this._bundle?.appName ?? "",
      permissions: (this._bundle?.permissions ?? []).map((p) => ({
        name: p.name,
        access: p.access,
      })),
      _metadata: {
        deploymentId: this._bundle?.deploymentId ?? "",
      },
    };

    // Add metadata fields if present
    const meta = this._bundle?.metadata;
    if (meta) {
      if (meta.runtimeVersion) {
        result._metadata.runtimeVersion = meta.runtimeVersion;
      }
      if (meta.bundleFiles && meta.bundleFiles.length > 0) {
        result._metadata.bundleFiles = [...meta.bundleFiles];
      }
      if (meta.packageJson) {
        result._metadata.packageJson = meta.packageJson as Record<string, unknown>;
      }
      if (meta.sdkVersion) {
        result._metadata.sdkVersion = meta.sdkVersion;
      }
    }

    return result;
  }
}

/** app.json format */
export interface AppJson {
  appName: string;
  permissions: Array<{ name: string; access: string }>;
  _metadata: AppJsonMetadata;
}

export interface AppJsonMetadata {
  deploymentId: string;
  runtimeVersion?: string;
  bundleFiles?: string[];
  packageJson?: Record<string, unknown>;
  sdkVersion?: string;
}
